import React, { ReactNode } from "react";
import Loading from "~/components/Loading";

export type SortOrder = "newest" | "oldest";

interface AppLayoutProps {
  title: string;
  children: ReactNode;
  loading?: boolean;
  error?: string | null;
  emptyTitle?: string;
  emptyMessage?: string;
  onRetry?: () => void;
  sortOrder?: SortOrder;
  onSortChange?: (order: SortOrder) => void;
}

export const AppLayout: React.FC<AppLayoutProps> = ({
  title,
  children,
  loading = false,
  error = null,
  emptyTitle = "No Content Available",
  emptyMessage = "We're working on adding new content. Please check back soon!",
  onRetry,
  sortOrder,
  onSortChange,
}) => {
  const handleSortToggle = () => {
    if (onSortChange) {
      const newOrder = sortOrder === "newest" ? "oldest" : "newest";
      onSortChange(newOrder);
    }
  };
  if (loading) {
    return (
      <div className="min-h-full w-full bg-[var(--background-primary)] transition-colors duration-300 animate-[fadeIn_0.3s_ease-in]">
        <div className="max-w-[1400px] mx-auto p-3 lg:p-8 min-h-[60vh]">
          <div className="flex items-center justify-between gap-4 mb-8">
            <h1 className="py-5 text-4xl md:text-5xl font-extrabold text-[var(--text-primary)] bg-gradient-to-r from-primary via-primary-light to-accent bg-clip-text text-transparent leading-tight">
              {title}
            </h1>
            {onSortChange && (
              <button
                onClick={handleSortToggle}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-[var(--border-color)] bg-[var(--background-secondary)] text-[var(--text-primary)] hover:bg-[var(--background-tertiary)] transition-colors duration-200 text-sm font-medium whitespace-nowrap"
                title={`Sort by ${sortOrder === "newest" ? "oldest" : "newest"} first`}
              >
                <span>📅</span>
                <span>{sortOrder === "newest" ? "Newest" : "Oldest"}</span>
              </button>
            )}
          </div>
          <Loading message={`Loading ${title.toLowerCase()}...`} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-full w-full bg-[var(--background-primary)] transition-colors duration-300 animate-[fadeIn_0.3s_ease-in]">
        <div className="max-w-[1400px] mx-auto p-8 min-h-[60vh]">
          <div className="flex items-center justify-between gap-4 mb-8">
            <h1 className="py-5 text-4xl md:text-5xl font-extrabold text-[var(--text-primary)] bg-gradient-to-r from-primary via-primary-light to-accent bg-clip-text text-transparent leading-tight">
              {title}
            </h1>
            {onSortChange && (
              <button
                onClick={handleSortToggle}
                className="flex items-center gap-2 px-4 py-2 rounded-lg border border-[var(--border-color)] bg-[var(--background-secondary)] text-[var(--text-primary)] hover:bg-[var(--background-tertiary)] transition-colors duration-200 text-sm font-medium whitespace-nowrap"
                title={`Sort by ${sortOrder === "newest" ? "oldest" : "newest"} first`}
              >
                <span>📅</span>
                <span>{sortOrder === "newest" ? "Newest" : "Oldest"}</span>
              </button>
            )}
          </div>
          <div className="text-center py-16 px-8">
            <div className="text-6xl md:text-7xl mb-4">⚠️</div>
            <h3 className="text-2xl md:text-3xl font-semibold text-[var(--text-primary)] mb-4">
              Error Loading Content
            </h3>
            <p className="text-lg text-[var(--text-secondary)] mb-6">{error}</p>
            {onRetry && (
              <button
                className="rounded-lg border-none px-6 py-3 text-base font-semibold cursor-pointer transition-all duration-200 inline-block bg-gradient-to-r from-primary via-primary-light to-accent text-white hover:bg-gradient-to-r hover:from-primary-light hover:via-accent hover:to-accent-hover hover:-translate-y-0.5 hover:shadow-lg active:translate-y-0"
                onClick={onRetry}
              >
                Try Again
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full w-full bg-[var(--background-primary)] transition-colors duration-300 animate-[fadeIn_0.3s_ease-in]">
      <div className="max-w-[1400px] mx-auto p-1 lg:p-8 min-h-[60vh]">
        <div className="flex items-center justify-between gap-4 mb-8">
          <h1 className="py-5 text-4xl md:text-5xl font-extrabold text-[var(--text-primary)] bg-gradient-to-r from-primary via-primary-light to-accent bg-clip-text text-transparent leading-tight">
            {title}
          </h1>
          {onSortChange && (
            <button
              onClick={handleSortToggle}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-[var(--border-color)] bg-[var(--background-secondary)] text-[var(--text-primary)] hover:bg-[var(--background-tertiary)] transition-colors duration-200 text-sm font-medium whitespace-nowrap"
              title={`Sort by ${sortOrder === "newest" ? "oldest" : "newest"} first`}
            >
              <span>📅</span>
              <span>{sortOrder === "newest" ? "Newest" : "Oldest"}</span>
            </button>
          )}
        </div>
        {React.Children.count(children) === 0 ? (
          <div className="text-center py-16 px-8 text-[var(--text-secondary)]">
            <div className="text-6xl md:text-7xl mb-4 opacity-50">📭</div>
            <h3 className="text-2xl md:text-3xl font-semibold text-[var(--text-primary)] mb-3">
              {emptyTitle}
            </h3>
            <p className="text-lg leading-relaxed max-w-[500px] mx-auto">
              {emptyMessage}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-[repeat(auto-fill,minmax(320px,1fr))] gap-8 mt-4">
            {children}
          </div>
        )}
      </div>
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @media (max-width: 1200px) {
          .content-grid {
            grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
            gap: 1.5rem;
          }
        }
        @media (max-width: 768px) {
          .page-content {
            padding: 1.5rem 1rem;
          }
          .content-grid {
            grid-template-columns: 1fr;
            gap: 1.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default AppLayout;
